import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "../services/api";
import { 
  PlayCircle, BookOpen, Calendar, ArrowRight, Star, Users, Globe, Monitor, Zap, Award, MessageCircle, Hash,
  CheckCircle, TrendingUp, Target, Cpu, Book, Video, UserCheck, BarChart, Briefcase, Smartphone, MapPin, Phone, Mail, Code, BrainCircuit, MessageSquare, Download
} from "lucide-react";
import { motion } from "framer-motion";
import toast, { Toaster } from 'react-hot-toast';
import PublicNavbar from "../components/PublicNavbar";
import PublicFooter from "../components/PublicFooter";
import { useBranding } from "../context/BrandingContext";

export default function LandingPage() {
  const navigate = useNavigate();
  const { branding } = useBranding();
  
  const [courses, setCourses] = useState([]);
  const [exams, setExams] = useState([]);
  const [liveClasses, setLiveClasses] = useState([]);
  const [landingConfig, setLandingConfig] = useState({});
  const [loading, setLoading] = useState(true);

  // Demo Modal State
  const [showDemoModal, setShowDemoModal] = useState(false);
  const [demoForm, setDemoForm] = useState({ name: '', email: '', phone: '', message: '' });

  const handleDemoSubmit = async (e) => {
    e.preventDefault();
    if (!demoForm.name || !demoForm.email) {
      toast.error("Please fill required fields");
      return;
    }
    try {
      await api.post("/saas/enquiries", demoForm);
      toast.success("Demo request submitted! We will contact you shortly.");
      setShowDemoModal(false);
      setDemoForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast.error("Failed to submit demo request.");
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [cRes, eRes, lRes, configRes] = await Promise.all([
          api.get("/courses").catch(() => ({ data: [] })),
          api.get("/exams/public/list").catch(() => ({ data: [] })),
          api.get("/live/public/list").catch(() => ({ data: [] })),
          api.get("/settings/landing_page_config").catch(() => ({ data: {} }))
        ]);
        setCourses(cRes.data || []);
        setExams(eRes.data || []);
        setLiveClasses(lRes.data || []);
        setLandingConfig(configRes.data || {});
      } catch (err) {
        console.error("Error fetching landing page data:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 font-['Inter',sans-serif]">
      <Toaster position="top-center" />
      <PublicNavbar />

      {/* 1. HERO SECTION ⭐ */}
      <section className="relative pt-24 pb-20 lg:pt-32 lg:pb-28 overflow-hidden bg-white">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center">
          <motion.div initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-50 text-indigo-600 rounded-full text-sm font-bold mb-6 border border-indigo-100">
              <Star size={16} fill="currentColor" /> #1 Smart Digital Education Platform
            </div>
            <h1 className="text-5xl lg:text-7xl font-black text-slate-900 leading-[1.1] mb-6 tracking-tight">
              {landingConfig.heroTitle || (
                <>
                  Transform Learning with <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-purple-600">Smart Digital Education</span>
                </>
              )}
            </h1>
            <p className="text-lg lg:text-xl text-slate-600 mb-8 leading-relaxed max-w-lg">
              {landingConfig.heroSubtitle || "Live classes, AI learning, online exams, and complete student progress tracking in one platform."}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 mb-10">
              <button 
                onClick={() => localStorage.getItem('token') ? navigate('/dashboard') : navigate('/register')}
                className="px-8 py-4 bg-indigo-600 text-white font-bold rounded-2xl shadow-xl shadow-indigo-200 hover:bg-indigo-700 transition-all text-lg flex items-center justify-center gap-2"
              >
                Start Learning <ArrowRight size={20} />
              </button>
              <button 
                onClick={() => setShowDemoModal(true)}
                className="px-8 py-4 bg-white text-indigo-600 font-bold rounded-2xl shadow-lg shadow-slate-100 border border-slate-200 hover:border-indigo-600 transition-all text-lg flex items-center justify-center gap-2"
              >
                Book Free Demo <PlayCircle size={20} />
              </button>
            </div>

            <div className="flex items-center gap-4">
              <div className="flex -space-x-4">
                {[1,2,3,4].map(i => (
                  <div key={i} className="w-12 h-12 rounded-full border-2 border-white bg-slate-200 flex items-center justify-center overflow-hidden">
                    <img src={`https://i.pravatar.cc/100?img=${i+10}`} alt="Student" />
                  </div>
                ))}
              </div>
              <div>
                <div className="flex text-amber-400">
                  {[1,2,3,4,5].map(i => <Star key={i} size={16} fill="currentColor" />)}
                </div>
                <p className="text-sm font-bold text-slate-600 mt-1">Trusted by <span className="text-indigo-600">{landingConfig.heroTrustedBy || "5000+ Students"}</span></p>
              </div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 30 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6, delay: 0.2 }} className="relative h-[600px] hidden lg:block">
            {/* Main Mockup */}
            <div className="absolute right-0 top-1/2 -translate-y-1/2 w-[90%] h-[80%] bg-slate-900 rounded-3xl shadow-2xl border-4 border-slate-800 overflow-hidden z-10 flex flex-col">
               <div className="h-6 bg-slate-800 w-full flex items-center px-4 gap-2">
                 <div className="w-2 h-2 rounded-full bg-red-500"></div>
                 <div className="w-2 h-2 rounded-full bg-yellow-500"></div>
                 <div className="w-2 h-2 rounded-full bg-green-500"></div>
               </div>
               <div className="flex-1 bg-slate-100 relative p-4 flex gap-4">
                 <div className="w-1/3 bg-white rounded-xl shadow p-4 space-y-4">
                    <div className="h-20 bg-indigo-50 rounded-lg"></div>
                    <div className="h-8 bg-slate-100 rounded-lg"></div>
                    <div className="h-8 bg-slate-100 rounded-lg"></div>
                 </div>
                 <div className="w-2/3 bg-white rounded-xl shadow relative overflow-hidden">
                    <img src={landingConfig.heroImage || "https://images.unsplash.com/photo-1522202176988-66273c2fd55f?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"} className="w-full h-full object-cover opacity-80" alt="Students Learning" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 to-transparent flex items-end p-6">
                      <div className="text-white">
                        <div className="flex items-center gap-2 mb-2">
                           <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse"></span> LIVE
                        </div>
                        <p className="font-bold">Advanced Mathematics</p>
                      </div>
                    </div>
                 </div>
               </div>
            </div>

            {/* Mobile App Preview */}
            <div className="absolute bottom-10 left-0 w-48 h-80 bg-white rounded-[2rem] shadow-2xl border-[6px] border-slate-900 z-20 overflow-hidden flex flex-col">
               <div className="bg-indigo-600 h-24 p-4 text-white">
                 <p className="text-xs font-bold opacity-80">Welcome back,</p>
                 <p className="font-black">Alex</p>
               </div>
               <div className="flex-1 p-3 space-y-3">
                 <div className="h-16 bg-slate-100 rounded-xl"></div>
                 <div className="h-16 bg-slate-100 rounded-xl"></div>
               </div>
            </div>

            {/* Floating Cards */}
            <motion.div animate={{ y: [0, -10, 0] }} transition={{ duration: 3, repeat: Infinity }} className="absolute top-10 left-10 bg-white p-4 rounded-2xl shadow-xl z-20 flex items-center gap-3 border border-slate-100">
              <div className="w-10 h-10 bg-green-100 text-green-600 rounded-full flex items-center justify-center"><CheckCircle size={20} /></div>
              <div><p className="text-xs font-bold text-slate-400">Success Rate</p><p className="font-black text-slate-800">95%</p></div>
            </motion.div>
            
            <motion.div animate={{ y: [0, 10, 0] }} transition={{ duration: 4, repeat: Infinity }} className="absolute bottom-20 -right-5 bg-white p-4 rounded-2xl shadow-xl z-20 flex items-center gap-3 border border-slate-100">
              <div className="w-10 h-10 bg-indigo-100 text-indigo-600 rounded-full flex items-center justify-center"><BrainCircuit size={20} /></div>
              <div><p className="text-xs font-bold text-slate-400">Feature</p><p className="font-black text-slate-800">AI Tutor</p></div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* 2. INSTITUTE HIGHLIGHTS */}
      <section className="py-12 bg-indigo-900 text-white relative z-20 -mt-8 mx-4 lg:mx-auto max-w-6xl rounded-3xl shadow-2xl overflow-hidden">
         <div className="absolute inset-0 bg-indigo-600 mix-blend-multiply opacity-50"></div>
         <div className="relative grid grid-cols-2 md:grid-cols-4 gap-8 px-8 md:px-16 py-8">
            <div className="text-center space-y-2">
              <p className="text-4xl md:text-5xl font-black text-amber-400">{landingConfig.statsStudents || "10k+"}</p>
              <p className="text-sm font-bold tracking-widest uppercase opacity-80">Students</p>
            </div>
            <div className="text-center space-y-2">
              <p className="text-4xl md:text-5xl font-black text-indigo-200">{landingConfig.statsTeachers || "150+"}</p>
              <p className="text-sm font-bold tracking-widest uppercase opacity-80">Teachers</p>
            </div>
            <div className="text-center space-y-2">
              <p className="text-4xl md:text-5xl font-black text-indigo-200">{landingConfig.statsCourses || "200+"}</p>
              <p className="text-sm font-bold tracking-widest uppercase opacity-80">Courses</p>
            </div>
            <div className="text-center space-y-2">
              <p className="text-4xl md:text-5xl font-black text-green-400">{landingConfig.statsResults || "95%"}</p>
              <p className="text-sm font-bold tracking-widest uppercase opacity-80">Results</p>
            </div>
         </div>
      </section>

      {/* 3. COURSES SECTION ⭐ */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Explore Our Programs</h2>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto text-lg">
              From school curriculum to advanced placement training, we have the right course for every stage of your journey.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { title: "School Courses", icon: <BookOpen />, desc: "Comprehensive syllabus coverage for classes 6th to 12th.", color: "bg-blue-50 text-blue-600" },
              { title: "Competitive Exams", icon: <Target />, desc: "Targeted preparation for JEE, NEET, UPSC, and SSC.", color: "bg-red-50 text-red-600" },
              { title: "Coding Courses", icon: <Code />, desc: "Learn Python, Java, Web Dev, and DSA from scratch.", color: "bg-emerald-50 text-emerald-600" },
              { title: "Graduation Programs", icon: <Award />, desc: "Semester prep and core subject mastery for college students.", color: "bg-purple-50 text-purple-600" },
              { title: "Placement Training", icon: <Briefcase />, desc: "Aptitude, resume building, and mock interviews.", color: "bg-amber-50 text-amber-600" }
            ].map((cat, i) => (
              <motion.div whileHover={{ y: -10 }} key={i} className="bg-white p-8 rounded-[2rem] shadow-sm hover:shadow-xl transition-all border border-slate-100 group">
                <div className={`w-16 h-16 rounded-2xl ${cat.color} flex items-center justify-center mb-6 group-hover:scale-110 transition-transform`}>
                  {React.cloneElement(cat.icon, { size: 32 })}
                </div>
                <h3 className="text-2xl font-black text-slate-800 mb-3">{cat.title}</h3>
                <p className="text-slate-500 mb-6 font-medium">{cat.desc}</p>
                <Link to="/public-courses" className="inline-flex items-center gap-2 text-indigo-600 font-bold hover:gap-4 transition-all">
                  Explore <ArrowRight size={18} />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. WHY CHOOSE US */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Why Choose Our Institute?</h2>
            <div className="w-24 h-1.5 bg-indigo-600 mx-auto rounded-full"></div>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: "Experienced Teachers", icon: <UserCheck />, desc: "Learn from industry experts and top educators." },
              { title: "AI Learning Support", icon: <BrainCircuit />, desc: "24/7 AI tutor for instant doubt resolution." },
              { title: "Live Interactive Classes", icon: <Video />, desc: "Engage in real-time with teachers and peers." },
              { title: "Placement Preparation", icon: <Briefcase />, desc: "Get ready for top companies with mock interviews." },
              { title: "Doubt Solving", icon: <MessageCircle />, desc: "Dedicated sessions to clear all your concepts." },
              { title: "Performance Analytics", icon: <BarChart />, desc: "Track progress with detailed reports and insights." }
            ].map((feature, i) => (
              <div key={i} className="flex gap-4 p-6 bg-slate-50 rounded-3xl border border-slate-100 hover:border-indigo-200 transition-colors">
                <div className="mt-1 w-12 h-12 bg-indigo-100 text-indigo-600 rounded-xl flex items-center justify-center shrink-0">
                  {React.cloneElement(feature.icon, { size: 24 })}
                </div>
                <div>
                  <h4 className="text-lg font-black text-slate-800 mb-2">{feature.title}</h4>
                  <p className="text-sm text-slate-500 font-medium leading-relaxed">{feature.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. AI LEARNING SECTION ⭐ */}
      <section className="py-32 relative bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/carbon-fibre.png')] opacity-20"></div>
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-indigo-600/30 blur-[120px] rounded-full"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 relative z-10 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-500/20 text-indigo-300 rounded-full text-sm font-bold mb-6 border border-indigo-500/30">
              <Zap size={16} /> Powered by Advanced AI
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">
              Meet Your <span className="text-transparent bg-clip-text bg-gradient-to-r from-indigo-400 to-cyan-400">AI Learning Assistant</span>
            </h2>
            <p className="text-lg text-slate-300 mb-10 leading-relaxed">
              Experience the future of education. Our AI adapts to your learning pace, predicts areas of improvement, and provides instant help.
            </p>
            
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { title: "Instant Doubt Solving", icon: <MessageSquare /> },
                { title: "Personalized Study Plans", icon: <Target /> },
                { title: "Smart Recommendations", icon: <TrendingUp /> },
                { title: "AI Generated Notes", icon: <BookOpen /> }
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-4 bg-white/5 p-4 rounded-2xl border border-white/10 hover:bg-white/10 transition-colors">
                  <div className="text-cyan-400">{React.cloneElement(item.icon, { size: 24 })}</div>
                  <span className="font-bold text-white text-sm">{item.title}</span>
                </div>
              ))}
            </div>
            
            <button onClick={() => localStorage.getItem('token') ? navigate('/ai-tutor') : navigate('/register')} className="mt-10 px-8 py-4 bg-gradient-to-r from-indigo-500 to-cyan-500 text-white font-black rounded-full shadow-lg hover:shadow-cyan-500/25 transition-all flex items-center gap-2">
              Try AI Tutor <ArrowRight size={20} />
            </button>
          </div>
          
          <div className="relative hidden md:block">
             <div className="aspect-square max-w-md mx-auto bg-gradient-to-br from-indigo-900 to-slate-800 rounded-full border border-white/10 shadow-2xl relative flex items-center justify-center p-8">
               <div className="absolute inset-0 rounded-full border border-cyan-400/30 animate-[spin_10s_linear_infinite]"></div>
               <div className="absolute inset-4 rounded-full border border-indigo-400/20 animate-[spin_15s_linear_infinite_reverse]"></div>
               <Cpu size={120} className="text-cyan-400 animate-pulse" />
               
               {/* Floating AI chat bubbles */}
               <div className="absolute top-10 -right-10 bg-slate-800 p-4 rounded-2xl rounded-tr-none border border-slate-700 shadow-xl max-w-[200px]">
                 <p className="text-xs text-slate-300 font-medium">"Here is the step-by-step solution for your Calculus problem."</p>
               </div>
               <div className="absolute bottom-20 -left-10 bg-indigo-900 p-4 rounded-2xl rounded-bl-none border border-indigo-700 shadow-xl max-w-[200px]">
                 <p className="text-xs text-white font-medium">"Based on your recent mock test, focus more on Thermodynamics."</p>
               </div>
             </div>
          </div>
        </div>
      </section>

      {/* 6. STUDENT SUCCESS STORIES ⭐ */}
      <section className="py-24 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Student Success Stories</h2>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto text-lg">
              Join thousands of students who have achieved their dreams with us.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {[
              { name: "Rahul S.", exam: "JEE Topper", text: "Cracked JEE with the help of AI practice tests. The doubt solving was instant!", img: "11" },
              { name: "Priya M.", exam: "Placed at Google", text: "The placement preparation and coding courses are top-notch. Mock interviews helped me a lot.", img: "5" },
              { name: "Amit K.", exam: "UPSC Cleared", text: "Structured live classes and comprehensive notes made my preparation journey smooth.", img: "12" }
            ].map((testimonial, i) => (
              <div key={i} className="bg-white p-8 rounded-[2rem] shadow-sm border border-slate-100 relative mt-8">
                <div className="absolute -top-10 left-8 w-20 h-20 rounded-full border-4 border-white overflow-hidden shadow-lg bg-slate-200">
                  <img src={`https://i.pravatar.cc/150?img=${testimonial.img}`} alt={testimonial.name} className="w-full h-full object-cover" />
                </div>
                <div className="mt-8 mb-4">
                  <div className="flex text-amber-400 mb-2">
                    {[1,2,3,4,5].map(star => <Star key={star} size={16} fill="currentColor" />)}
                  </div>
                  <h4 className="text-xl font-black text-slate-800">{testimonial.name}</h4>
                  <p className="text-sm font-bold text-indigo-600">{testimonial.exam}</p>
                </div>
                <p className="text-slate-600 italic leading-relaxed">"{testimonial.text}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. LIVE CLASSES SECTION */}
      <section className="py-24 bg-white overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center">
          <div className="order-2 lg:order-1 relative hidden md:block">
             <div className="aspect-video bg-slate-900 rounded-3xl shadow-2xl overflow-hidden border border-slate-200 relative">
               <img src={landingConfig.liveClassImage || "https://images.unsplash.com/photo-1577896851231-70ef18881754?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80"} alt="Live Class" className="w-full h-full object-cover opacity-60" />
               <div className="absolute inset-0 flex items-center justify-center">
                 <div className="w-20 h-20 bg-white/20 backdrop-blur-md rounded-full flex items-center justify-center cursor-pointer hover:scale-110 transition-transform">
                   <PlayCircle size={48} className="text-white" />
                 </div>
               </div>
               <div className="absolute top-4 left-4 bg-red-500 text-white px-3 py-1 rounded-full text-xs font-bold flex items-center gap-2">
                 <span className="w-2 h-2 bg-white rounded-full animate-pulse"></span> LIVE
               </div>
               
               {/* Chat Overlay */}
               <div className="absolute bottom-4 right-4 w-64 bg-white/90 backdrop-blur rounded-xl p-3 shadow-lg max-h-40 overflow-hidden">
                 <div className="space-y-2 text-xs">
                   <p><span className="font-bold text-indigo-600">Ravi:</span> Sir, can you repeat step 2?</p>
                   <p><span className="font-bold text-indigo-600">Teacher:</span> Sure, let's go over it again.</p>
                   <p><span className="font-bold text-indigo-600">Aisha:</span> Understood! ✅</p>
                 </div>
               </div>
             </div>
          </div>
          
          <div className="order-1 lg:order-2">
            <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tight">Interactive Live Classes</h2>
            <p className="text-lg text-slate-500 mb-8 leading-relaxed">
              Experience the energy of a real classroom from the comfort of your home. Interact, ask questions, and learn together.
            </p>
            
            <ul className="space-y-4 mb-8">
              {[
                "Real-time interaction with top faculty",
                "Access to recorded lectures anytime",
                "Digital homework and assignments",
                "Automated attendance tracking"
              ].map((item, i) => (
                <li key={i} className="flex items-center gap-3 font-medium text-slate-700">
                  <CheckCircle size={20} className="text-green-500" /> {item}
                </li>
              ))}
            </ul>
            
            <button onClick={() => navigate('/public-live-classes')} className="px-8 py-4 bg-slate-900 text-white font-bold rounded-xl shadow-lg hover:bg-indigo-600 transition-colors">
              View Schedule
            </button>
          </div>
        </div>
      </section>

      {/* 8. PARENT DASHBOARD SECTION */}
      <section className="py-24 bg-indigo-50">
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <h2 className="text-4xl font-black text-slate-900 mb-6 tracking-tight">Dedicated Parent Dashboard</h2>
            <p className="text-lg text-slate-600 mb-8 leading-relaxed">
              Stay involved in your child's education. Get real-time updates and detailed insights into their academic journey.
            </p>
            
            <div className="grid grid-cols-2 gap-6">
              {[
                { title: "Attendance Tracking", icon: <Calendar /> },
                { title: "Marks & Results", icon: <BarChart /> },
                { title: "Homework Status", icon: <Book /> },
                { title: "Instant Notifications", icon: <Smartphone /> }
              ].map((item, i) => (
                <div key={i} className="bg-white p-4 rounded-2xl shadow-sm border border-slate-100 flex items-center gap-3">
                  <div className="text-indigo-600 bg-indigo-50 p-2 rounded-lg">{React.cloneElement(item.icon, { size: 20 })}</div>
                  <span className="font-bold text-sm text-slate-800">{item.title}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative hidden md:block">
             <div className="bg-white rounded-[2rem] shadow-2xl p-6 border border-slate-200">
               <div className="flex items-center justify-between mb-6">
                 <div>
                   <h3 className="font-black text-lg">Student Progress</h3>
                   <p className="text-xs text-slate-400 font-bold">Rohan Kumar (Class 10)</p>
                 </div>
                 <div className="bg-green-100 text-green-600 px-3 py-1 rounded-full text-xs font-bold">Excellent</div>
               </div>
               
               <div className="space-y-4">
                 <div>
                   <div className="flex justify-between text-sm font-bold mb-1"><span className="text-slate-600">Mathematics</span><span>92%</span></div>
                   <div className="w-full bg-slate-100 rounded-full h-2"><div className="bg-indigo-600 h-2 rounded-full w-[92%]"></div></div>
                 </div>
                 <div>
                   <div className="flex justify-between text-sm font-bold mb-1"><span className="text-slate-600">Science</span><span>88%</span></div>
                   <div className="w-full bg-slate-100 rounded-full h-2"><div className="bg-indigo-600 h-2 rounded-full w-[88%]"></div></div>
                 </div>
                 <div>
                   <div className="flex justify-between text-sm font-bold mb-1"><span className="text-slate-600">Attendance</span><span>95%</span></div>
                   <div className="w-full bg-slate-100 rounded-full h-2"><div className="bg-green-500 h-2 rounded-full w-[95%]"></div></div>
                 </div>
               </div>
               
               <div className="mt-6 p-4 bg-indigo-50 rounded-xl flex gap-4">
                 <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center shrink-0 shadow-sm"><Target size={20} className="text-indigo-600"/></div>
                 <div>
                   <p className="text-sm font-bold text-slate-800">Teacher Note</p>
                   <p className="text-xs text-slate-500 mt-1">"Rohan is performing exceptionally well in algebra."</p>
                 </div>
               </div>
             </div>
          </div>
        </div>
      </section>

      {/* 9. PLACEMENT & CAREER SECTION ⭐ */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
             <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-600 rounded-full text-sm font-bold mb-6 border border-green-100">
              <Briefcase size={16} /> Career Readiness
            </div>
            <h2 className="text-4xl font-black text-slate-900 mb-4 tracking-tight">Your Launchpad to Success</h2>
            <p className="text-slate-500 font-medium max-w-2xl mx-auto text-lg">
              For graduation students. We don't just teach, we prepare you for the industry.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mb-16">
             {[
               { title: "Coding Prep", icon: <Code /> },
               { title: "Mock Interviews", icon: <Users /> },
               { title: "Internships", icon: <Award /> },
               { title: "Top Placements", icon: <TrendingUp /> }
             ].map((item, i) => (
               <div key={i} className="text-center p-8 bg-slate-50 rounded-3xl border border-slate-100 hover:bg-white hover:shadow-xl transition-all">
                 <div className="w-16 h-16 bg-white rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-sm text-indigo-600">
                   {React.cloneElement(item.icon, { size: 32 })}
                 </div>
                 <h4 className="font-black text-slate-800">{item.title}</h4>
               </div>
             ))}
          </div>

          <div>
            <p className="text-center font-bold text-slate-400 uppercase tracking-widest text-sm mb-8">Our Students Are Placed At</p>
            <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16 opacity-60 grayscale hover:grayscale-0 transition-all duration-500">
              <div className="text-2xl font-black text-slate-800 flex items-center gap-2"><Globe/> TechCorp</div>
              <div className="text-2xl font-black text-slate-800 flex items-center gap-2"><Monitor/> InnovateAI</div>
              <div className="text-2xl font-black text-slate-800 flex items-center gap-2"><Cpu/> SysTech</div>
              <div className="text-2xl font-black text-slate-800 flex items-center gap-2"><Zap/> GlobalNet</div>
            </div>
          </div>
        </div>
      </section>

      {/* 10. MOBILE APP SECTION */}
      <section className="py-24 bg-slate-900 overflow-hidden relative">
        <div className="absolute right-0 top-0 w-1/2 h-full bg-indigo-600 skew-x-12 translate-x-32 hidden lg:block opacity-20"></div>
        <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-16 items-center relative z-10">
          <div>
            <h2 className="text-4xl md:text-5xl font-black text-white mb-6 leading-tight">Learn Anywhere, Anytime.</h2>
            <p className="text-lg text-slate-300 mb-8 leading-relaxed">
              Download our mobile app and carry your classroom in your pocket. Seamless sync across all devices.
            </p>
            
            <ul className="space-y-4 mb-10">
              <li className="flex items-center gap-3 font-medium text-slate-200"><CheckCircle className="text-green-400"/> Study on the go</li>
              <li className="flex items-center gap-3 font-medium text-slate-200"><CheckCircle className="text-green-400"/> Offline learning & downloads</li>
              <li className="flex items-center gap-3 font-medium text-slate-200"><CheckCircle className="text-green-400"/> Instant push notifications</li>
            </ul>

            <div className="flex flex-wrap gap-4">
              <button onClick={() => toast('iOS App coming soon!', { icon: '🍎' })} className="flex items-center gap-3 px-6 py-3 bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-100 transition-colors">
                <Download size={24} /> <div><p className="text-[10px] uppercase leading-none opacity-60">Download on</p><p className="leading-none mt-1">App Store</p></div>
              </button>
              <a href="/app-release.apk" download="LMS-Student-App.apk" className="flex items-center gap-3 px-6 py-3 bg-white text-slate-900 rounded-xl font-bold hover:bg-slate-100 transition-colors">
                <Smartphone size={24} /> <div><p className="text-[10px] uppercase leading-none opacity-60">Download APK</p><p className="leading-none mt-1">Android App</p></div>
              </a>
            </div>
          </div>

          <div className="relative h-[500px] hidden md:flex justify-center items-center">
             {/* Simple App Mockup Illustration */}
             <div className="w-64 h-[450px] bg-white rounded-[3rem] p-2 shadow-2xl relative z-20 border-8 border-slate-800 rotate-[-5deg]">
               <div className="w-full h-full bg-slate-50 rounded-[2.5rem] overflow-hidden flex flex-col">
                 <div className="bg-indigo-600 p-4 pt-8 text-white">
                   <p className="font-bold">Dashboard</p>
                 </div>
                 <div className="p-4 space-y-4 flex-1">
                   <div className="h-24 bg-white rounded-xl shadow-sm border border-slate-100"></div>
                   <div className="h-24 bg-white rounded-xl shadow-sm border border-slate-100"></div>
                   <div className="h-24 bg-white rounded-xl shadow-sm border border-slate-100"></div>
                 </div>
               </div>
             </div>
             <div className="w-64 h-[450px] bg-slate-800 rounded-[3rem] p-2 shadow-2xl absolute z-10 border-8 border-slate-700 rotate-[10deg] translate-x-16">
                <div className="w-full h-full bg-slate-900 rounded-[2.5rem] overflow-hidden flex flex-col">
                  <div className="p-4 pt-8 text-white"><p className="font-bold">Live Class</p></div>
                  <div className="flex-1 bg-slate-800 m-2 rounded-xl flex items-center justify-center border border-slate-700"><PlayCircle size={40} className="text-slate-600" /></div>
                </div>
             </div>
          </div>
        </div>
      </section>

      {/* 11. CTA SECTION (Very Important) */}
      <section className="py-24 bg-gradient-to-br from-indigo-600 to-purple-700 text-white text-center px-4 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="max-w-4xl mx-auto relative z-10">
          <h2 className="text-5xl lg:text-6xl font-black mb-6 tracking-tight">{landingConfig.ctaTitle || "Start Your Learning Journey Today"}</h2>
          <p className="text-xl text-indigo-100 mb-12 font-medium">{landingConfig.ctaSubtitle || "Join 10,000+ students who are already learning and achieving their goals."}</p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <button onClick={() => localStorage.getItem('token') ? navigate('/dashboard') : navigate('/register')} className="px-10 py-5 bg-white text-indigo-600 font-black rounded-full shadow-2xl hover:scale-105 transition-transform text-lg">
              Enroll Now
            </button>
            <button onClick={() => setShowDemoModal(true)} className="px-10 py-5 bg-indigo-800/50 text-white font-black rounded-full border border-indigo-400/30 hover:bg-indigo-800 transition-colors text-lg backdrop-blur-sm">
              Book Demo
            </button>
            <button onClick={() => navigate('/contact')} className="px-10 py-5 bg-transparent text-white font-black rounded-full border-2 border-white hover:bg-white hover:text-indigo-600 transition-colors text-lg">
              Contact Us
            </button>
          </div>
        </div>
      </section>

      {/* DEMO MODAL */}
      {showDemoModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-white rounded-[2rem] shadow-2xl w-full max-w-md overflow-hidden relative">
            <button onClick={() => setShowDemoModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 w-8 h-8 flex items-center justify-center bg-slate-100 rounded-full font-bold">✕</button>
            <div className="bg-indigo-600 p-8 text-center text-white">
              <h3 className="text-2xl font-black mb-2">Book a Free Demo</h3>
              <p className="text-indigo-100 text-sm font-medium">Experience our platform with an expert.</p>
            </div>
            <div className="p-8">
              <form onSubmit={handleDemoSubmit} className="space-y-4">
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Full Name *</label>
                  <input type="text" required value={demoForm.name} onChange={e => setDemoForm({...demoForm, name: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium text-sm" placeholder="Enter your name" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Email Address *</label>
                  <input type="email" required value={demoForm.email} onChange={e => setDemoForm({...demoForm, email: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium text-sm" placeholder="Enter your email" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Phone Number</label>
                  <input type="tel" value={demoForm.phone} onChange={e => setDemoForm({...demoForm, phone: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium text-sm" placeholder="Enter your phone" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-widest mb-1.5">Message / Course of Interest</label>
                  <textarea rows="2" value={demoForm.message} onChange={e => setDemoForm({...demoForm, message: e.target.value})} className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 transition-all font-medium resize-none text-sm" placeholder="I am interested in..."></textarea>
                </div>
                <button type="submit" className="w-full mt-2 py-4 bg-indigo-600 text-white font-black rounded-xl shadow-lg hover:bg-indigo-700 transition-colors">
                  Request Demo
                </button>
              </form>
            </div>
          </motion.div>
        </div>
      )}

      {/* 12. FOOTER */}
      <PublicFooter />
    </div>
  );
}
